import { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DarkPage } from "@/components/app/PageHeader";
import { PhoneCall, Clock, MessageSquare, ChevronDown } from "lucide-react";

const LIVE_HOURS_START = 8; // 8am
const LIVE_HOURS_END = 18; // 6pm

const STATUS_STYLES = {
  open: "text-[#00F5D4] bg-[#00F5D4]/10 border-[#00F5D4]/20",
  pending: "text-amber-400 bg-amber-500/10 border-amber-500/20",
  resolved: "text-blue-400 bg-blue-500/10 border-blue-500/20",
  closed: "text-[#7d8590] bg-[#1c2128] border-[#30363d]",
};

const EMPTY_FORM = { subject: "", body: "" };

/** Whether "now" (the visitor's own local clock) falls inside the live
 *  support window. Each viewer is judged against their own timezone. */
const isWithinLiveHours = (date) => {
  const hour = date.getHours();
  return hour >= LIVE_HOURS_START && hour < LIVE_HOURS_END;
};

const formatHour = (h) => {
  const period = h < 12 ? "am" : "pm";
  const display = h % 12 === 0 ? 12 : h % 12;
  return `${display}${period}`;
};

export default function LiveSupport() {
  const [now, setNow] = useState(() => new Date());
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [requests, setRequests] = useState([]);
  const [ticketDetail, setTicketDetail] = useState(null);
  const [reply, setReply] = useState("");

  // Re-check the live window every minute so the banner flips the moment
  // 6pm (or 8am) passes without needing a page refresh.
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(id);
  }, []);

  const live = useMemo(() => isWithinLiveHours(now), [now]);

  const loadRequests = useCallback(async () => {
    try {
      const { data } = await api.get("/help/tickets");
      setRequests((data || []).filter((t) => t.channel === "live_support"));
    } catch {
      // Silent — this list is a convenience, not the primary action on this page.
    }
  }, []);

  useEffect(() => { loadRequests(); }, [loadRequests]);

  const submit = async () => {
    if (!form.subject.trim() || !form.body.trim()) return toast.error("Subject and description are required");
    setBusy(true);
    try {
      await api.post("/help/tickets", {
        subject: form.subject,
        body: form.body,
        priority: live ? "urgent" : "normal",
        channel: "live_support",
      });
      toast.success(live ? "Support team alerted — someone will be with you shortly." : "Message sent — we'll respond as soon as we're back online.");
      setForm(EMPTY_FORM);
      loadRequests();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send request");
    } finally {
      setBusy(false);
    }
  };

  const openRequest = async (ticket) => {
    try {
      const { data } = await api.get(`/help/tickets/${ticket.ticket_id}`);
      setTicketDetail(data);
    } catch {
      toast.error("Failed to load request");
    }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await api.post(`/help/tickets/${ticketDetail.ticket_id}/messages`, { body: reply });
      setReply("");
      const { data } = await api.get(`/help/tickets/${ticketDetail.ticket_id}`);
      setTicketDetail(data);
      loadRequests();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send reply");
    } finally {
      setBusy(false);
    }
  };

  if (ticketDetail) {
    return (
      <DarkPage><div className="p-6 max-w-2xl mx-auto">
        <button onClick={() => setTicketDetail(null)} className="text-xs text-[#7d8590] hover:text-white mb-4 flex items-center gap-1">
          <ChevronDown className="w-3 h-3 rotate-90" /> Back to Live Support
        </button>
        <div className="flex items-start justify-between mb-4">
          <h1 className="text-xl font-bold text-white">{ticketDetail.subject}</h1>
          <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLES[ticketDetail.status]}`}>{ticketDetail.status}</span>
        </div>
        <div className="space-y-3 mb-4">
          {ticketDetail.messages.map((m) => (
            <div key={m.message_id} className="bg-[#0d1117] border border-[#21262d] rounded-lg p-4">
              <div className="text-xs text-[#7d8590] mb-1">
                <span className="text-white font-medium">{m.author_name}</span> · {new Date(m.created_at).toLocaleString()}
              </div>
              <div className="text-sm text-[#c9d1d9] whitespace-pre-wrap">{m.body}</div>
            </div>
          ))}
        </div>
        {ticketDetail.status !== "closed" && (
          <div className="space-y-2">
            <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={3} placeholder="Write a reply…"
              className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm resize-none" />
            <Button onClick={sendReply} disabled={busy} className="bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
              {busy ? "Sending…" : "Send reply"}
            </Button>
          </div>
        )}
      </div></DarkPage>
    );
  }

  return (
    <DarkPage><div className="p-6 max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <PhoneCall className="w-5 h-5 text-[#7d8590]" /> Live Support
        </h1>
        <p className="text-sm text-[#7d8590] mt-1">
          Get connected with the support team directly, {formatHour(LIVE_HOURS_START)}–{formatHour(LIVE_HOURS_END)} your time, every day.
        </p>
      </div>

      <div
        data-testid="live-support-status"
        className={`flex items-center gap-3 rounded-lg border px-4 py-3 mb-6 ${
          live ? "bg-[#00F5D4]/10 border-[#00F5D4]/20 text-[#00F5D4]" : "bg-amber-500/10 border-amber-500/20 text-amber-400"
        }`}
      >
        <span className={`relative flex h-2.5 w-2.5 shrink-0`}>
          {live && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00F5D4] opacity-75" />}
          <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${live ? "bg-[#00F5D4]" : "bg-amber-400"}`} />
        </span>
        <div className="text-sm">
          {live ? (
            <span><strong>Live support is available now.</strong> Submit a request below and a team member will jump in.</span>
          ) : (
            <span>
              <strong>Outside live support hours</strong> ({formatHour(LIVE_HOURS_START)}–{formatHour(LIVE_HOURS_END)}). Send a message and
              we'll get back to you as soon as we're back online.
            </span>
          )}
        </div>
        <Clock className="w-4 h-4 ml-auto shrink-0 opacity-70" />
      </div>

      <div className="bg-[#0d1117] border border-[#21262d] rounded-lg p-5 mb-8">
        <div className="space-y-4">
          <div>
            <label className="text-xs text-[#7d8590] mb-1 block">What do you need help with? *</label>
            <input value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
              placeholder="Short summary"
              className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-xs text-[#7d8590] mb-1 block">Details *</label>
            <textarea value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} rows={4}
              placeholder="Describe what's going on…"
              className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm resize-none" />
          </div>
          <Button onClick={submit} disabled={busy} className="bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
            {busy ? "Sending…" : live ? "Request live support" : "Send message"}
          </Button>
        </div>
      </div>

      <div>
        <h2 className="text-sm font-semibold text-white mb-3">Your recent live support requests</h2>
        {requests.length === 0 ? (
          <div className="text-center py-10 text-[#7d8590] text-sm border border-dashed border-[#21262d] rounded-lg">
            No live support requests yet.
          </div>
        ) : (
          <div className="bg-[#0d1117] border border-[#21262d] rounded-lg overflow-hidden">
            {requests.map((t) => (
              <button key={t.ticket_id} onClick={() => openRequest(t)}
                className="w-full text-left flex items-center justify-between px-4 py-3 border-b border-[#21262d] last:border-0 hover:bg-[#161b22] transition-colors">
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-3.5 h-3.5 text-[#7d8590]" />
                  <span className="text-sm text-white">{t.subject}</span>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLES[t.status]}`}>{t.status}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div></DarkPage>
  );
}
